import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { ScrollSync } from 'react-virtualized';
import styles from './SubAssemblyTable.module.css';
import {
  ASN_STATUS,
} from '../../constants';
import {
  getFormattedAsn,
  getSelectedOptionalColumns,
  filterByNestedVcDescriptionCurry,
  compareByDateAndSerialNumber,
} from '../../utils/tableUtils';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { optionalColumns } from '../../staticData';
import VirtualizedTable from '../VirtualizedTable/VirtualizedTable';
import StatusCircle from '../StatusCircle/StatusCircle';
import HeaderSearchBar from '../HeaderSearchBar.js/HeaderSearchBar';

const COLORS = {
  RED: 'red',
  GREEN: 'green',
  YELLOW: 'yellow',
  BLUE: 'blue',
  WHITE: 'white',
};

const HeaderWithCounter = ({
  headerText,
  count,
  countBackgroundColor = '#48b275',
  itemsAlignment = 'center',
}) => (
  <div
    className={styles.headerWithCounterContainer}
    style={{ alignItems: itemsAlignment }}
  >
    <div
      className={styles.readyPartsCount}
      style={{ backgroundColor: countBackgroundColor }}
    >
      {count}
    </div>
    <div className={styles.headerText}>{headerText}</div>
  </div>
);

const SubAssemblyTable = ({
  rows = [],
  selectedOptionalColNames = [],
  countData,
  subAssemblyConfigs = [],
}) => {
  const ROW_HEIGHT = 38;
  const HEADER_ROW_HEIGHT = 50;
  const PADDING_ROWS_COUNT = 10;
  const [rowsData, setRowsData] = useState([...rows]);
  const [descriptionSearchText, setDescriptionSearchText] = useState('');
  const [vcNumberSearchText, setVcNumberSearchText] = useState('');
  const [plant] = usePlantAndLinesStore().plantState;
  const isLucknowProlifePlant = plant?.label === 'Lucknow-Prolife';
  const parentName = isLucknowProlifePlant ? 'Parent' : 'VC';

  const getDynamicRowHeight = () => ROW_HEIGHT;
  const getRowHeight = ({ index }) =>
    index === 0 ? HEADER_ROW_HEIGHT : getDynamicRowHeight(index);
  const tableRowColors = ['#1e1e1e', '#272727'];

  const chassisStatusColorIdentifier = (status) => {
    if (status === ASN_STATUS.LINED_OUT) return COLORS.RED;
    if (status === ASN_STATUS.DROPPED) return COLORS.YELLOW;
    if (status === ASN_STATUS.READY_FOR_DROP) return COLORS.WHITE;
    if (
      status === ASN_STATUS.ROLLED_OUT ||
      status === ASN_STATUS.WTCL ||
      status === ASN_STATUS.SFLT
    )
      return COLORS.GREEN;
    return null;
  };

  const identifyTextColorClassOfRowForDropTime = ({
    emergencyFlag,
    lineOnFlag,
    status,
  }) => {
    if (emergencyFlag || lineOnFlag) return styles.yellowText;
    if (status === ASN_STATUS.LINED_OUT) return styles.redText;
    return styles.greenText;
  };

  const identifyTextColorClassOfRowForAsn = (row) => {
    const isLinedOutAsn = row.status === ASN_STATUS.LINED_OUT;
    const isAsnOutOfSequence = row.emergencyFlag || row.lineOnFlag;
    if (isLinedOutAsn) {
      return styles.redText;
    } else if (isAsnOutOfSequence) {
      return styles.yellowText;
    }
    return styles.greenText;
  };

  const isSubAssemblyDispatched = (row, colSubName) => {
    if (!row) return false;
    const targetName = colSubName ? colSubName.toLowerCase().trim() : '';

    if (row[colSubName] === 'DISPATCHED' || row[colSubName] === 'DROPPED' || row[colSubName] === 'READY' || row[colSubName] === true) {
      return true;
    }

    if (Array.isArray(row.subassembly) && row.subassembly.length > 0) {
      const sub = row.subassembly.find((s) => {
        if (!s.subAssemblyName) return false;
        const sName = s.subAssemblyName.toLowerCase().trim();
        return (
          sName === targetName ||
          sName.includes(targetName) ||
          targetName.includes(sName) ||
          sName.split(' ')[0] === targetName.split(' ')[0]
        );
      });
      if (sub && (sub.printFlag === true || sub.status === 'DISPATCHED' || sub.status === 'DROPPED' || sub.status === 'READY')) {
        return true;
      }
    }

    return false;
  };

  const fixedColumns = [
    {
      width: 55,
      title: '#',
      field: 'sequence',
      render: (row, idx) => idx,
    },
    {
      width: 120,
      title: 'ASN',
      field: 'asn',
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: (row) => (
        <span className={identifyTextColorClassOfRowForAsn(row)}>
          {getFormattedAsn(row)}
        </span>
      ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="ASN"
          count={rowsData.length}
          countBackgroundColor="#a76100"
          itemsAlignment="baseline"
        />
      ),
    },
    {
      width: 300,
      title: 'Description',
      field: 'materialDescription',
      cellStyles: {
        fontWeight: 'bold',
      },
      render: (row) => row?.materialDescription || row?.vcDescription || '-',
      renderHeader: () => (
        <HeaderSearchBar
          title="Description"
          handleChange={(e) => setDescriptionSearchText(e.target.value)}
          clearData={() => setDescriptionSearchText('')}
          placeholder={`Search ${parentName} Description`}
          value={descriptionSearchText}
          iconProps={{ className: styles.searchIcon }}
        />
      ),
    },
    {
      width: 70,
      title: 'Shift',
      field: 'shift',
      headerStyles: { textAlign: 'center' },
      render: (row) => {
        const currentShiftLabel = row?.shiftName || row?.shift;
        if (currentShiftLabel)
          return (
            <div
              style={{
                width: '22px',
                height: '22px',
                lineHeight: '22px',
                borderRadius: '5px',
                backgroundColor: '#52A281',
                color: 'white',
                textAlign: 'center',
                marginTop: '-4px',
                marginLeft: '1em',
              }}
            >
              {currentShiftLabel}
            </div>
          );
        return null;
      },
    },
    {
      width: 240,
      title: 'Vc',
      field: 'materialNumber',
      cellStyles: {
        fontWeight: 'bold',
      },
      render: (row) => row?.materialNumber || row?.vc || '-',
      renderHeader: () => (
        <HeaderSearchBar
          title={isLucknowProlifePlant ? 'Part No.' : 'VC'}
          handleChange={(e) => setVcNumberSearchText(e.target.value)}
          clearData={() => setVcNumberSearchText('')}
          placeholder={
            isLucknowProlifePlant
              ? 'Search Part Number'
              : 'Search VC Number'
          }
          value={vcNumberSearchText}
          iconProps={{ className: styles.searchIcon }}
        />
      ),
    },
  ];

  useEffect(() => {
    const filteredData = rows.filter((row) => {
      const descText = row.materialDescription || row.vcDescription || '';
      const vcText = row.materialNumber || row.vc || '';
      const descriptionMatch = descriptionSearchText
        ? descText.toLowerCase().includes(descriptionSearchText.toLowerCase()) || filterByNestedVcDescriptionCurry(descriptionSearchText)(row)
        : true;
      const vcNumberMatch = vcNumberSearchText
        ? vcText.toLowerCase().includes(vcNumberSearchText.toLowerCase())
        : true;

      return descriptionMatch && vcNumberMatch;
    });

    const sortedDataByDateAndSerialNumber = filteredData.sort((a, b) => {
      const getStatusRank = (status) => {
        if (status === ASN_STATUS.READY_FOR_DROP || status === 'READY_FOR_DROP') return 1;
        if (status === ASN_STATUS.DROPPED || status === 'DROPPED') return 2;
        return 3;
      };
      const rankA = getStatusRank(a.status);
      const rankB = getStatusRank(b.status);
      if (rankA !== rankB) return rankA - rankB;
      return compareByDateAndSerialNumber(a, b, 'productionDate', 'sequence');
    });
    setRowsData(sortedDataByDateAndSerialNumber);
  }, [descriptionSearchText, vcNumberSearchText, rows]);

  const fixedColumnsStyled = fixedColumns.map((column) => ({
    ...column,
    headerStyles: {
      color: '#A76100',
      fontSize: '1em',
      fontWeight: '600',
      boxSizing: 'border-box',
      ...column.headerStyles,
    },
  }));

  const optionalColumnsStyles = {
    headerStyles: {
      color: '#fff',
      zIndex: 1,
      boxSizing: 'border-box',
    },
  };

  const selectedOptionalColumns = getSelectedOptionalColumns(
    optionalColumns,
    selectedOptionalColNames,
    optionalColumnsStyles,
  ).map((col) => ({
    ...col,
    render: (row) => {
      if (!row) return '-';
      const field = col.field;
      if (field === 'colour') {
        const desc = row.attributes?.colourDescription || row.colourDescription;
        const code = row.attributes?.colourCode || row.colourCode;
        if (desc && code) return `${desc} (${code})`;
        if (desc) return desc;
        if (code) return code;
      }
      const valFromAttr = row.attributes ? row.attributes[field] : undefined;
      const valFromRow = row[field];
      const val = valFromAttr !== undefined && valFromAttr !== null ? valFromAttr : valFromRow;
      if (val === undefined || val === null || val === '') return '-';
      return String(val);
    },
  }));

  const subAssemblyListToUse =
    subAssemblyConfigs && subAssemblyConfigs.length > 0
      ? subAssemblyConfigs.map((c) => c.subAssemblyName || c)
      : [];

  const SUB_ASSEMBLIES_COLUMNS = subAssemblyListToUse.map((subAssyName) => {
    const dispatchedCount = rowsData.filter((r) => isSubAssemblyDispatched(r, subAssyName)).length;
    return {
      width: 130,
      title: subAssyName,
      field: subAssyName,
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: (row) => {
        const isDispatched = isSubAssemblyDispatched(row, subAssyName);
        const color = isDispatched ? COLORS.BLUE : null;
        return <StatusCircle color={color} hoverText={subAssyName} />;
      },
      renderHeader: () => (
        <HeaderWithCounter
          headerText={subAssyName}
          count={dispatchedCount}
          countBackgroundColor="#0275a7"
        />
      ),
    };
  });

  const standardDataColumns = [
    {
      width: 100,
      title: 'Drop Time',
      field: '_dropTime',
      style: { textAlign: 'center' },
      cellStyles: {
        background: '#000',
      },
      headerStyles: {
        color: '#D8D8D8',
      },
      render: (row) =>
        row.droppedAt ? (
          <span className={identifyTextColorClassOfRowForDropTime(row)}>
            {moment(row.droppedAt).format('hh:mm a')}
          </span>
        ) : (
          '-'
        ),
    },
    {
      width: 100,
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      title: 'Chassis',
      field: 'status',
      cellStyles: {
        background: '#000',
      },
      style: { textAlign: 'center', whiteSpace: 'normal' },
      render: ({ status, reason }) => (
        <StatusCircle
          color={chassisStatusColorIdentifier(status)}
          hoverText={reason}
        />
      ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="Chassis"
          count={countData?.data?.chassisReadiness || 0}
        />
      ),
    },
  ];

  const baseColumns = () => {
    return [
      standardDataColumns[0],
      standardDataColumns[1],
      ...SUB_ASSEMBLIES_COLUMNS,
    ];
  };

  return (
    <>
      <div style={{ height: '95%' }} className={styles.container}>
        <div style={{ height: '100%' }} className={styles.tableView}>
          <ScrollSync>
            {({ onScroll, scrollTop }) => (
              <div
                style={{ height: '100%' }}
                className={clsx(styles.sequenceScroll)}
              >
                <VirtualizedTable
                  tableWidth={'44%'}
                  columns={[...fixedColumnsStyled, ...selectedOptionalColumns]}
                  rows={rowsData}
                  enableAutoHeight
                  rowHeight={getRowHeight}
                  onScroll={onScroll}
                  scrollTop={scrollTop}
                  classNames={{
                    tableContainerClassName:
                      styles.attributesTableChildMaterial,
                    tableHeadClassName: styles.tableHead,
                    tableCellClassName: styles.mTablecell,
                  }}
                  rowColors={tableRowColors}
                  fixedColumnCount={2}
                  classNameBottomLeftGrid={clsx(
                    styles.BottomLft,
                    styles.hideVerticalScroll,
                  )}
                  classNameBottomRightGrid={clsx(
                    styles.BottomRgt,
                    styles.hideVerticalScroll,
                    styles.scroll,
                  )}
                  classNameTopLeftGrid={styles.TopLft}
                  classNameTopRightGrid={styles.TopRgt}
                  maxEmptyRowPadding={PADDING_ROWS_COUNT}
                />
                <VirtualizedTable
                  tableWidth={'55%'}
                  columns={[...baseColumns()]}
                  enableAutoHeight
                  rows={rowsData}
                  rowHeight={getRowHeight}
                  onScroll={onScroll}
                  scrollTop={scrollTop}
                  classNames={{
                    tableContainerClassName: clsx(
                      styles.attributesTable,
                      styles.RgtTable,
                    ),
                    tableHeadClassName: styles.tableHead,
                    tableCellClassName: styles.mTablecell,
                  }}
                  rowColors={tableRowColors}
                  classNameBottomLeftGrid={clsx(
                    styles.BottomLast,
                    styles.scroll,
                  )}
                  classNameBottomRightGrid={clsx(
                    styles.BottomRgt,
                    styles.scroll,
                  )}
                  classNameTopLeftGrid={styles.TopLft}
                  classNameTopRightGrid={styles.TopRgt}
                  maxEmptyRowPadding={PADDING_ROWS_COUNT}
                />
              </div>
            )}
          </ScrollSync>
        </div>
      </div>
    </>
  );
};

SubAssemblyTable.propTypes = {
  rows: PropTypes.array,
  selectedOptionalColNames: PropTypes.arrayOf(PropTypes.string),
  countData: PropTypes.object,
  subAssemblyConfigs: PropTypes.array,
};

export default SubAssemblyTable;
